/**
 * NP Gummies — PDP Avatar Content Swap
 * Built 2026-08-31 (swarm build, forge domain) — syncs avatar targeting across
 * the full funnel: ad creative -> Landra lander / Perspective quiz -> this PDP.
 *
 * Reads ?avatar=<slug> from the current URL on page load and swaps the
 * above-the-fold headline, hero image and description copy to match the
 * angle the visitor just read on their entry lander/quiz. Copy pulled from
 * brands/np-gummies/meta-launch-avatars-2026-08-31.md (the same 6 avatars
 * used to build the 6 Landra landers + 6 Perspective quizzes, 2026-08-31).
 *
 * 2026-09-17 STICKY AVATAR: a valid ?avatar= slug is now PERSISTED the same
 * way np-offer-assign.js persists the offer — np_avatar cookie (90 days) plus
 * an np_avatar_backup localStorage key. With no URL param the slug is read
 * from the cookie, then the localStorage backup, so a returning/direct
 * visitor keeps their avatar copy alongside their sticky offer price instead
 * of reverting to the generic default. A valid URL slug always wins and
 * re-persists (it is copy, not price).
 *
 * No avatar param (or a slug not in the table) AND nothing valid stored ->
 * apply() is a no-op and the default section-settings content (already
 * server-rendered) stands as-is. This is intentional: organic/direct/untagged
 * traffic must never break.
 *
 * Loaded as a classic (non-module, non-async, non-defer) <script src> tag
 * immediately after the three target elements in sections/np-buybox.liquid,
 * so it executes synchronously, in document order, before the rest of the
 * page below it parses/paints -- that ordering IS the flicker-avoidance
 * mechanism (a same-paint-frame swap, not a post-load re-flow).
 */
(function () {
  "use strict";

  // avatar slug -> { headline, key, sub (description/lede), quote (optional testimonial),
  //                  frames (optional: ordered src-substring preferences for the hero frame) }
  //
  // 2026-09-01 FIX (Jared, reply to msg 48060: "its still showing a massive gummy
  // for the product photos and wont show anything else"): the 08-31 build gave
  // each avatar an `image` (np-gummy-a..f.png — single-gummy 3D renders) and
  // INSERTED it into .bm-stage as a new <img class="on">. That was the whole
  // bug, twice over: (1) the hero became a giant gummy render instead of a
  // product photo, and (2) the gallery then had 15 frames vs 14 thumbs, so
  // assets/np.js's gallery init bailed on `thumbs.length !== frames.length`
  // and every thumbnail went dead ("won't show anything else"). The swap now
  // ACTIVATES an existing real-photo frame (see HERO_FRAMES) and never adds
  // or removes gallery nodes, so the thumb/frame mapping np.js relies on is
  // untouched and the gallery keeps working.
  var AVATAR_BLOCKS = {
    "wired-tired": {
      headline: "You’re Not Anxious. But You’re Not Calm Either.",
      key: "Calm",
      sub: "Meet the Hum — the low-level static that’s just on, all day. Ashwagandha, magnesium and L-theanine, built for daytime. No melatonin, so you stay sharp.",
      quote: "“It read me embarrassingly well. Two gummies and the Hum finally turns down.”"
    },
    "label-skeptic": {
      headline: "The First Honest Label In Your Supplement Drawer",
      key: "Honest",
      sub: "No proprietary blend. Every dose printed — Ashwagandha 100mg, Magnesium 50mg, L-Theanine 15mg. Read it yourself, that’s the entire pitch.",
      quote: "“I read every label before I buy. This is the first one that just told me the numbers.”"
    },
    "3pm-crasher": {
      headline: "Built For 3PM. Not 10PM.",
      key: "3PM",
      sub: "The 3PM Reset: Ashwagandha, L-theanine and magnesium for the afternoon edge — no melatonin, so you’re still sharp for the rest of your day.",
      quote: "“My 4PM self is nicer to everyone now. Same me, just not fried by 3.”"
    },
    "overstimulated-mom": {
      headline: "The Half-Second Between The Noise And Your Reaction",
      key: "Half-Second",
      sub: "For the mom who is touched out by 4PM. Ashwagandha, magnesium and L-theanine in a daytime gummy with no melatonin, so you get back the half-second before the snap and stay fully there for pickup.",
      quote: "“I’m still the same mom. I just get the half-second between the noise and my reaction now.”"
    },
    "cortisol-girl": {
      headline: "Cortisol Isn’t The Villain. Your Off-Switch Is Stuck.",
      key: "Off-Switch",
      sub: "A daytime formula that supports a healthy stress response — Ashwagandha, magnesium, L-theanine. Every dose on the label. No melatonin.",
      quote: "“Genuinely the most accurate 2 minutes of my week was the quiz. This is what it pointed me to.”"
    },
    // 2026-09-16 AVATAR 7 "The Mom They Ask" (identification / earned-status row,
    // merged into the persona canon 2026-09-15). Beat order carried onto the PDP:
    // envy of her calm -> not your fault -> the right tool. She is not calmer,
    // she is better equipped. Fresh copy (novelty-checked), never lifted from
    // avatars 1-6. Real product photos only, no frame swaps needed.
    "mom-they-ask": {
      headline: "She Isn’t Calmer Than You. She’s Better Equipped.",
      key: "Equipped",
      sub: "The one small thing the mom at pickup does at 2PM: ashwagandha, magnesium and L-theanine, built for daytime. No melatonin, so you’re sharp for pickup and steady through dinner. You were never the problem. It was never your fault that you didn’t have it yet.",
      quote: "“My sister-in-law asked what I’d changed. Same me, there’s just room in me now.”"
    },
    // 2026-09-27 AVATAR 14 "The Stress Weight Loop" (the body row, added to the
    // canon 2026-09-26). Implied-weight Tier 1 on the PDP: the loop is named in
    // her own words, the product line stays stress + the cravings that ride with
    // it, no weight outcome, no "lowers cortisol", no numbers. Fresh copy
    // (novelty-checked), never lifted from avatars 1-13. Real product photos only.
    "stress-weight-loop": {
      headline: "It Was Never Your Willpower. It Was The Top Of The Loop.",
      key: "Loop",
      sub: "Stress all day, the pantry at 9 PM, a 3 AM wake-up, a new plan on Monday. Ashwagandha, magnesium and L-theanine for the stress at the top of that loop, built for daytime. No melatonin, so you stay sharp at work. Sits next to whatever plan you already have.",
      quote: "\u201cI didn\u2019t change my plan at all. I just stopped standing in the pantry at 9 PM.\u201d"
    },
    // 2026-09-27 AVATAR 15 "The Switched-Off Wife" (the desire row, added to the
    // canon 2026-09-26). Claim line: stress kills desire, less stress means more
    // room for it; the product line is the stress half, never a dose-level
    // libido promise, none of Meta's auto-reject words, never "you" + symptom.
    // Fresh copy (novelty-checked). Real product photos only.
    "switched-off-wife": {
      headline: "It Was Never You. It Was Never Him. It Was The List.",
      key: "List",
      sub: "Stress is the desire killer nobody talks about. Ashwagandha, magnesium and L-theanine for the head that runs the house at 9:30 PM, built for daytime. No melatonin, so you stay sharp at work and softer at home. Not a pill for the bedroom. A gummy for the list.",
      quote: "\u201cI bumped his hip at the stove for no reason and didn\u2019t start listing things.\u201d"
    },
    "wine-oclock-swap": {
      headline: "It Was Never The Wine. It Was The Moment The Day Was Over.",
      key: "Moment",
      sub: "The glass was never the point. The end of the day was. Ashwagandha, magnesium and L-theanine for the stress that builds all afternoon, built for daytime. No melatonin, so you stay sharp at work and still awake for bath time. Not a rule about what you pour. A new first move at six.",
      quote: "\u201cI eat two with lunch, and by the time I walk in the day is already over in my head.\u201d"
    },
    // 2026-10-15 AVATAR 17 "Coming Off a Shift" (Landra suggestion narrowpath-shift-09, OFFICIAL,
    // full flow build). Fresh copy from brands/np-gummies/avatar17-funnel-copy-2026-10-15.md.
    "coming-off-shift": {
      headline: "The Shift Ended At Seven. Your Body Didn't Get The Memo.",
      key: "Memo",
      sub: "Wired, not tired, for hours after you clock out. Ashwagandha, magnesium and L-theanine for everyday stress, built for daytime. No melatonin and nothing that makes you drowsy, so you come down without being knocked out and still have your drive home and your night.",
      quote: "\u201cI eat two on my last break, and by the time I'm home I'm actually off.\u201d"
    },
    // 2026-09-28 AVATAR 8 "The On-Call Daughter" (promoted OFFICIAL 2026-09-28, full
    // flow build). Fresh copy from brands/np-gummies/avatar8-funnel-copy-2026-09-28.md,
    // never lifted from avatars 1-7/14/15. Real product photos only, no frame swaps.
    "on-call-daughter": {
      headline: "Her Calendar Has Two Colors. Only One Of Them Is Hers.",
      key: "Two Colors",
      sub: "One phone holds two people's weeks, and the body never fully clocks out. Ashwagandha, magnesium and L-theanine support a healthy stress response for the woman holding the calendar, built for daytime. There's no melatonin, so she stays sharp for the callback and the pickup. It was made for the middle of her day, never as a stand-in for anyone's care.",
      quote: "\"I still take every call. I just stopped holding the phone during my own meetings.\""
    },
    // 2026-09-28 AVATAR 9 "The Short Fuse" (promoted OFFICIAL 2026-09-28, full
    // flow build). Fresh copy from brands/np-gummies/avatar9-funnel-copy-2026-09-28.md,
    // never lifted from avatars 1-7/14/15. Real product photos only, no frame swaps.
    "short-fuse": {
      headline: "The Towel Was Never The Problem. The Fuse Just Needed Less On It.",
      key: "Fuse",
      sub: "A flicker of irritation used to stay a flicker. Ashwagandha, magnesium and L-theanine for the everyday stress that has been landing on a shorter fuse, built for daytime. No melatonin, so the drop-off and the 5 PM kitchen both stay clear. It works on the stress, never on the people in the house.",
      quote: "\"I found the towel on the bed again, hung it up and went downstairs, and that was the entire event.\""
    },
    // 2026-09-28 AVATAR 10 "The One Who Can't Be Tired" (promoted OFFICIAL 2026-09-28, full
    // flow build). Fresh copy from brands/np-gummies/avatar10-funnel-copy-2026-09-28.md,
    // never lifted from avatars 1-7/14/15. Real product photos only, no frame swaps.
    "cant-be-tired": {
      headline: "Nobody Ever Made Anything For The Hours Before Seven.",
      key: "Before Seven",
      sub: "Foggy isn't an option at seven, so she's never had a single thing for the wait that wasn't caffeine. Ashwagandha, magnesium and L-theanine for the afternoon before a stretch of nights and the drive in, built for daytime. No melatonin, because it isn't a sleep aid and it never goes to work with her. Every dose is printed on the label.",
      quote: "\"I sat down and read for an hour instead of refilling the coffee.\""
    },
    // 2026-09-28 AVATAR 11 "The One Who Can't Stop" (promoted OFFICIAL 2026-09-28, full
    // flow build). Fresh copy from brands/np-gummies/avatar11-funnel-copy-2026-09-28.md,
    // never lifted from avatars 1-7/14/15. Real product photos only, no frame swaps.
    "cant-stop": {
      headline: "The Sign Says Closed. The Owner Is On Standby.",
      key: "Standby",
      sub: "A business you own never turns off, it goes to standby, and standby stays on through dinner. Ashwagandha, magnesium and L-theanine for the person whose name is on every paycheck, built for daytime. No melatonin, so there's no fog from the gummy itself on a day nobody else can cover. It does nothing for the books. It's for the person keeping them.",
      quote: "\"I sat through a whole movie with my wife and never once paused it to check my email.\"",
      // 2026-10-04 (Jared, reply to msg 59063: "yes with the male pics"): cant-stop is the
      // owner/mixed row, so like steady-operator every gallery frame that shows a woman is
      // re-pointed, for THIS avatar only, at the men-focused real-bottle lifestyle set
      // (theme assets np-steady-life-*.jpg, 1200x1200, same files steady-operator uses).
      // All other avatars keep the default female product media.
      frame_swaps: [
        { match: "np-pdp-model-jar",    src: "np-steady-life-1.jpg", cap: "Calm, in hand",
          alt: "A man in his workshop holding a bottle of Natures Relax Gummies" },
        { match: "np-pdp-model-bite",   src: "np-steady-life-2.jpg", cap: "In good company",
          alt: "A dad in his kitchen holding a bottle of Natures Relax Gummies, his kid playing behind him" },
        { match: "2026-08-3014.36.47",  src: "np-steady-life-3.jpg", cap: "Two gummies. Back to steady.",
          alt: "A man on his porch taking a Natures Relax gummy, bottle in hand" },
        { match: "2026-08-3014.37.43",  src: "np-steady-life-4.jpg", cap: "Fits in one hand",
          alt: "A man's hand holding a bottle of Natures Relax Gummies" },
        { match: "2026-08-3014.38.55",  src: "np-steady-life-5.jpg", cap: "Steady through the workday",
          alt: "A man at his desk holding a bottle of Natures Relax Gummies" }
      ]
    },
    // 2026-09-28 AVATAR 12 "The No-End-Date Parent" (promoted OFFICIAL 2026-09-28, full
    // flow build). Fresh copy from brands/np-gummies/avatar12-funnel-copy-2026-09-28.md,
    // never lifted from avatars 1-7/14/15. Real product photos only, no frame swaps.
    "no-end-date-parent": {
      headline: "Nobody Signed The Permission Slip That Says Being Tired Is Allowed. Consider It Signed.",
      key: "Permission Slip",
      sub: "The day has no end date, and being tired inside it was never a failing. Ashwagandha, magnesium and L-theanine support a healthy stress response, built for daytime with no melatonin, so nothing dulls the part of you that has to stay sharp at 8 AM. It doesn't touch the schedule, it takes the edge off the one carrying it.",
      quote: "\"I gave a friend the real answer in daylight instead of 'just busy' at 11 PM.\""
    },
    // 2026-09-28 AVATAR 13 "The One Who Waits" (promoted OFFICIAL 2026-09-28, full
    // flow build). Fresh copy from brands/np-gummies/avatar13-funnel-copy-2026-09-28.md,
    // never lifted from avatars 1-7/14/15. Real product photos only, no frame swaps.
    "one-who-waits": {
      headline: "Every Clock In Her House Runs On Two Time Zones.",
      key: "Two Time Zones",
      sub: "This one is for the grown-up holding the whole house for months and doing the time-zone math on every clock she passes. Ashwagandha, magnesium and L-theanine support a healthy stress response, and it's built for daytime. There's no melatonin, so she stays sharp for the 7 AM school run and sounds like herself on every call. It takes the edge off the person watching the clock and the quiet phone. It doesn't touch either one.",
      quote: "\"I stopped adding nine hours to the microwave clock every time I walked past it.\""
    },
    "steady-operator": {
      headline: "Steady. Not Sedated.",
      key: "Steady",
      sub: "Ashwagandha, magnesium, L-theanine — widens the gap between the trigger and the reaction. No fog, no melatonin, full horsepower.",
      quote: "“Same output. Fewer flipped tables. My wife noticed before I did.”",
      // 2026-09-01 PM (Jared, msg 48142: "for the man steady operator niche is
      // it possible to do more men focused images instead? this is the only
      // avatar i want to actually change some of the pics for it to be men
      // focused"). Every gallery frame that shows a woman is re-pointed, for
      // THIS avatar only, at a men-focused lifestyle photo of the real bottle
      // (theme assets np-steady-life-*.jpg, 1200x1200). `match` is a substring
      // of the current product-media src; the frame keeps its DOM node and
      // index — only src/alt/data-cap change (see applyFrameSwaps).
      frame_swaps: [
        { match: "np-pdp-model-jar",        src: "np-steady-life-1.jpg", cap: "Calm, in hand",
          alt: "A man in his workshop holding a bottle of Natures Relax Gummies" },
        { match: "np-pdp-model-bite",         src: "np-steady-life-2.jpg", cap: "In good company",
          alt: "A dad in his kitchen holding a bottle of Natures Relax Gummies, his kid playing behind him" },
        { match: "2026-08-3014.36.47",  src: "np-steady-life-3.jpg", cap: "Two gummies. Back to steady.",
          alt: "A man on his porch taking a Natures Relax gummy, bottle in hand" },
        { match: "2026-08-3014.37.43",  src: "np-steady-life-4.jpg", cap: "Fits in one hand",
          alt: "A man's hand holding a bottle of Natures Relax Gummies" },
        { match: "2026-08-3014.38.55",  src: "np-steady-life-5.jpg", cap: "Steady through the workday",
          alt: "A man at his desk holding a bottle of Natures Relax Gummies" }
      ]
    }
  };

  // Ordered src-substring preferences for the avatar hero. All are REAL product
  // photos already in the product's Shopify media (never the np-gummy-*.png
  // renders): the open bottle with gummies spilled out first, the packshot as
  // fallback, then any non-video frame. A block may override with its own
  // `frames` list; unmatched -> the gallery is left exactly as rendered.
  var HERO_FRAMES = ["np-gummy-spill", "np-bottle"];

  // Product name shown in the big serif h2 on every avatar page (2026-09-01).
  // A block may override with its own `product_name`.
  //
  // 2026-09-01 PM (Jared, msg 48142): was "Daytime Calm Gummies" — Jared:
  // "it should be 'Natures Relax Gummies'". The h2 now carries the REAL
  // product name (matches the bottle label, cart line, checkout and header);
  // the daytime positioning lives in the eyebrow hook + lede, not the name.
  // The brand words get the neutral .hl-key marker so np-spritz.js squiggles
  // "Natures Relax" (not the final word "Gummies") — same rule the default
  // no-avatar h2 follows in sections/np-buybox.liquid.
  var PRODUCT_NAME = "Natures Relax Gummies";
  var PRODUCT_KEY = "Natures Relax";

  // Fill `el` with `text`, wrapping `key` (if present) in a .hl-key span.
  // DOM nodes only — never innerHTML of copy.
  function fillWithKey(el, text, key) {
    el.textContent = "";
    var kIdx = key ? text.indexOf(key) : -1;
    if (kIdx === -1) { el.textContent = text; return; }
    el.appendChild(document.createTextNode(text.slice(0, kIdx)));
    var span = document.createElement("span");
    span.className = "hl-key";
    span.textContent = key;
    el.appendChild(span);
    el.appendChild(document.createTextNode(text.slice(kIdx + key.length)));
  }

  function frameSrc(el) {
    return (el.currentSrc || el.getAttribute("src") || el.getAttribute("poster") || "");
  }

  // Theme-asset URL for `name`, built from the /cdn/shop/t/<n>/assets/ prefix
  // of an asset already on the page (this script's own <script src> first,
  // then any asset-path img/script/link). Never hardcodes a theme version:
  // the prefix is whatever theme is actually serving the page. Returns null
  // when no asset URL can be found — callers then leave the frame alone.
  var _assetPrefix;
  function assetUrl(name) {
    if (_assetPrefix === undefined) {
      _assetPrefix = null;
      var cands = [];
      try {
        if (document.currentScript && document.currentScript.src) cands.push(document.currentScript.src);
        var own = document.querySelector('script[src*="np-avatar-swap"]');
        if (own && own.src) cands.push(own.src);
        var any = document.querySelectorAll('script[src*="/assets/"], link[href*="/assets/"], img[src*="/assets/"]');
        for (var a = 0; a < any.length; a++) cands.push(any[a].src || any[a].href || "");
      } catch (e) { /* fall through */ }
      for (var c = 0; c < cands.length; c++) {
        var m = /^(.*\/cdn\/shop\/t\/\d+\/assets\/)/.exec(cands[c] || "");
        if (m) {
          _assetPrefix = m[1];
          // Carry this script's own ?v= stamp onto the swapped assets as a
          // cache-buster: Shopify's CDN serves the UN-versioned asset URL from
          // a long-lived cache, so a re-pushed image would otherwise stay
          // stale. The stamp changes on every JS push, which is exactly when a
          // re-fetch is wanted; it is never a hardcoded theme version.
          var v = /[?&]v=(\d+)/.exec(cands[c]);
          _assetVer = v ? "?v=" + v[1] : "";
          break;
        }
      }
    }
    return _assetPrefix ? _assetPrefix + name + _assetVer : null;
  }
  var _assetVer = "";

  // Re-point existing gallery frames at per-avatar assets (block.frame_swaps).
  // Mutates src/srcset/alt/data-cap on the matched <img> and the same-index
  // .bm-th thumb img ONLY — never adds or removes nodes, so assets/np.js's
  // `thumbs.length !== frames.length` bail-out can never trip. Returns the
  // number of frames swapped.
  function applyFrameSwaps(frames, thumbs, swaps) {
    if (!swaps || !swaps.length) return 0;
    var n = 0;
    for (var s = 0; s < swaps.length; s++) {
      var sw = swaps[s];
      var url = assetUrl(sw.src);
      if (!url) return n;
      for (var i = 0; i < frames.length; i++) {
        var fr = frames[i];
        if (fr.tagName !== "IMG" || frameSrc(fr).indexOf(sw.match) === -1) continue;
        fr.removeAttribute("srcset");
        fr.removeAttribute("sizes");
        fr.setAttribute("src", url);
        if (sw.alt) fr.setAttribute("alt", sw.alt);
        if (sw.cap) fr.setAttribute("data-cap", sw.cap);
        fr.setAttribute("data-frame-swapped", sw.src);
        if (thumbs.length === frames.length) {
          var th = thumbs[i];
          var thImg = th.querySelector("img");
          if (thImg) {
            thImg.removeAttribute("srcset");
            thImg.removeAttribute("sizes");
            thImg.setAttribute("src", url);
          }
          if (sw.cap) th.setAttribute("aria-label", sw.cap);
        }
        n++;
        break;
      }
    }
    return n;
  }

  function pickFrame(frames, prefs) {
    // 2026-09-01 (Jared): the "Real Calm" product VIDEO is the first-load
    // media on every avatar page too — same as the default PDP — not the
    // static bottle photo. First VIDEO frame wins; photo prefs are the
    // fallback for a product whose media has no video.
    for (var v = 0; v < frames.length; v++) {
      if (frames[v].tagName === "VIDEO") return v;
    }
    for (var p = 0; p < prefs.length; p++) {
      for (var i = 0; i < frames.length; i++) {
        if (frames[i].tagName === "IMG" && frameSrc(frames[i]).indexOf(prefs[p]) !== -1) return i;
      }
    }
    for (var j = 0; j < frames.length; j++) {
      if (frames[j].tagName === "IMG") return j;
    }
    return -1;
  }

  // ---- persistence (2026-09-17), mirrors np-offer-assign.js ----
  var COOKIE_NAME = "np_avatar";
  var LS_KEY = "np_avatar_backup";
  var COOKIE_DAYS = 90;

  function getCookie(name) {
    try {
      var m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
      return m ? decodeURIComponent(m[1]) : null;
    } catch (e) { return null; }
  }
  function setCookie(name, value, days) {
    try {
      var d = new Date();
      d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
      var secure = window.location.protocol === "https:" ? "; Secure" : "";
      document.cookie = name + "=" + encodeURIComponent(value) +
        "; expires=" + d.toUTCString() + "; path=/; SameSite=Lax" + secure;
    } catch (e) { /* non-fatal */ }
  }
  function getLocal(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }
  function setLocal(key, value) {
    try { window.localStorage.setItem(key, value); } catch (e) { /* storage disabled — cookie is still primary */ }
  }
  // Normalise before validating: np-vcid.js (runs earlier, in <head>) also
  // writes the raw ?avatar= value into an np_avatar cookie + localStorage
  // "np_avatar" for Voluum cart attribution, unvalidated and uncased. A
  // shared cookie name is deliberate (one avatar of record for attribution
  // AND rendering); this script only ever ACTS on a value that maps to a
  // known block, so a raw/unknown value stored by vcid stays a render no-op.
  function normSlug(v) {
    return typeof v === "string" ? v.trim().toLowerCase() : null;
  }

  // ---- 2026-10-24 DIRECT PRODUCT PAGE TEST (Jared, Hermes chat: "test going straight to the
  // product page ... It should include the language of the avatar flow that it come in with").
  // AVATAR_FIT fills #np-avatar-fit in sections/np-buybox.liquid, PRODUCT TEMPLATE ONLY
  // (body.tpl-product): `bullets` = 3 lines lifted VERBATIM from that avatar's "8 Signs"
  // listicle (/pages/8-signs-<slug>), `hook` = that avatar's Landra lander H1, verbatim.
  // No new claims, no rewording. Source dump: .context/worker_evidence/np_direct_pdp/avatar_copy_source.json
  var AVATAR_FIT = {
    "wired-tired": {
      hook: "You're Not in Crisis. But You Never Switch Off. Meet \"the Hum,\" the Background App That Never Closes",
      bullets: ["Your stress never clocks out, so neither does your body", "Tired but wired by dinner, every single night", "Your jaw is tight by noon and you didn't even notice"]
    },
    "label-skeptic": {
      hook: "Why Skeptical Label-Readers Are Quietly Switching to the Calm Gummy That Prints All Six Doses",
      bullets: ["The front of the bottle says calm and the back says proprietary blend", "Every calm product you own was built for bedtime", "You'd rather feel a little better on something honest than foggy on something strong"]
    },
    "3pm-crasher": {
      hook: "Why Working Women Are Reaching for Calm at 3PM, Not Bedtime",
      bullets: ["Your morning is great and your afternoon is a different person", "The post-lunch coffee makes you jittery and tense instead of awake", "Everything built to help says 'take before bed'"]
    },
    "overstimulated-mom": {
      hook: "Why Moms Are Reaching for a Daytime Calm That Doesn't Slow Them Down at School Pickup",
      bullets: ["Every sound in the house lands on you at once", "The snap comes out before you've decided to say anything", "Anything that helps makes you foggy at pickup"]
    },
    "cortisol-girl": {
      hook: "Cortisol Isn't the Villain — Your OFF-Switch Is Stuck, and a Daytime Gummy Is Quietly Changing How Women Reset at 3PM",
      bullets: ["You can't finish a yawn", "Regulating has become a second job", "Nothing made for calm can be taken before 9PM"]
    },
    "steady-operator": {
      hook: "Men Who Run Everything Are Trading Two Fingers of Bourbon for Something That Actually Has a Lower Gear",
      bullets: ["Your brain doesn't clock out when you do", "Two fingers of bourbon is your current off-switch", "You're wound up and you'd never say stressed"]
    },
    "mom-they-ask": {
      hook: "The Mom at Pickup Who Never Looks Rushed Is Doing One Boring Thing at 2PM",
      bullets: ["You've thought 'must be nice' about the same woman twice this week", "Your body runs on stress from 6AM to midnight", "Anything that helps makes you foggy when you need to be the grown-up"]
    },
    "on-call-daughter": {
      hook: "Women Holding Two Calendars in One Phone Are Trying a Daytime Gummy for the Stress of Being the One Who Picks Up",
      bullets: ["Your phone lights up with a familiar number and your whole body answers before you do", "You haven't really relaxed in years", "You can't take anything that dulls you"]
    },
    "short-fuse": {
      hook: "Women Who Snapped at a Wet Towel This Week Are Trying a Daytime Gummy for the Stress Underneath the Short Fuse",
      bullets: ["A cupboard left open sends you from zero to a voice you don't recognize", "You knew it was too much while you were saying it", "You've been apologizing for a version of you that isn't you"]
    },
    "cant-be-tired": {
      hook: "Nurses Who Pack Lunch at 4:40 AM Are Trying a Daytime Gummy for the Hours Before a Shift, Not for Sleep",
      bullets: ["The lunch is packed by 4:40 and you're still not in the car", "Caffeine on one side, a sleep aid on the other, nothing for the middle", "Your body clock gave up trying to settle"]
    },
    "cant-stop": {
      hook: "Owners Whose Sign Says Closed at Six Are Trying a Daytime Gummy for the Standby Stress That Never Clocks Out",
      bullets: ["The sign says Closed and nobody told you", "Your mind runs like a spin cycle", "The bedtime thing stopped working the year the business started"]
    },
    "no-end-date-parent": {
      hook: "Parents With No End Date on the Calendar Are Trying a Daytime Gummy for the Stress of the Day They Carry Alone",
      bullets: ["You say 'we're good, just busy' and save the real answer for 11PM", "Your calendar is other people's appointments", "You can't take anything that makes you foggy"]
    },
    "one-who-waits": {
      hook: "Why Moms Holding the House Alone Are Reaching for a Daytime Calm That Doesn't Slow Them Down",
      bullets: ["You do the time-zone math on every clock you pass", "Your heart sinks when the usual message doesn't come", "Whatever you take has to leave you clear, because nobody else is getting up with the kids"]
    },
    "stress-weight-loop": {
      hook: "Women Who Quit Fighting the 9 PM Pantry Are Aiming at the Stress, Not the Snack",
      bullets: ["You're not hungry at 9:14PM and you're standing in the pantry anyway", "Every plan attacked the food and never the stress", "Nighttime calm gummies left you foggy and the loop intact"]
    },
    "switched-off-wife": {
      hook: "Women Carrying the Whole House in Their Heads Are Trying a Daytime Gummy for the 9:30 PM List That Won't Stop",
      bullets: ["The chore list is still running when he says goodnight", "You're touched out before anyone your own size gets near you", "You never stopped loving him, you just never got a minute off"]
    },
    "wine-oclock-swap": {
      hook: "Women Who Open the Wine Fridge Before Their Coat Is Off Are Trying a Daytime Gummy for the Stress Underneath the Six O'Clock Pour",
      bullets: ["The fridge door opens before your coat does", "Two glasses stopped doing what two glasses used to do", "It was never the wine, it was the day being over"]
    },
    "coming-off-shift": {
      hook: "Women Coming Off a 12-Hour Shift Wired, Not Tired, Are Trying a Daytime Gummy That Helps Them Come Down Without Being Knocked Out",
      bullets: ["You clock out exhausted and get home wide awake", "You sit in the car before you can drive home", "Everything on the shelf is for getting through the shift or knocking you out after it"]
    }
  };

  function applyFit(slug) {
    try {
      var box = document.getElementById("np-avatar-fit");
      if (!box) return;
      var onProduct = !!(document.body && document.body.classList && document.body.classList.contains("tpl-product"));
      var fit = AVATAR_FIT[slug];
      if (!onProduct || !fit) { box.hidden = true; return; }
      var list = document.getElementById("np-fit-list");
      if (list) {
        list.textContent = "";
        for (var i = 0; i < fit.bullets.length; i++) {
          var li = document.createElement("li");
          li.textContent = fit.bullets[i];
          list.appendChild(li);
        }
      }
      var hook = document.getElementById("np-fit-hook");
      if (hook) hook.textContent = fit.hook || "";
      box.hidden = false;
      box.setAttribute("data-avatar-fit", slug);
    } catch (e) { /* non-fatal: default page stands */ }
  }

  function isValidSlug(v) {
    return typeof v === "string" && v.length > 0 && Object.prototype.hasOwnProperty.call(AVATAR_BLOCKS, v);
  }
  function persist(slug) {
    setCookie(COOKIE_NAME, slug, COOKIE_DAYS);
    setLocal(LS_KEY, slug);
  }

  function getUrlAvatarSlug() {
    try {
      var params = new URLSearchParams(window.location.search);
      var slug = params.get("avatar");
      return slug ? slug.trim().toLowerCase() : null;
    } catch (e) {
      return null;
    }
  }

  // Resolve the avatar for this page view: { slug, source } or null.
  //   1. valid ?avatar= in the URL -> persist, source "url_param"
  //   2. valid np_avatar cookie      -> source "cookie" (LS backup re-synced)
  //   3. valid np_avatar_backup LS   -> cookie restored, source "localStorage_restore"
  //   else null -> caller no-ops, default server-rendered content stands.
  function resolveAvatar() {
    var url = getUrlAvatarSlug();
    if (isValidSlug(url)) {
      persist(url);
      return { slug: url, source: "url_param" };
    }
    var c = normSlug(getCookie(COOKIE_NAME));
    if (isValidSlug(c)) {
      if (getLocal(LS_KEY) !== c) setLocal(LS_KEY, c);
      return { slug: c, source: "cookie" };
    }
    var b = normSlug(getLocal(LS_KEY));
    if (isValidSlug(b)) {
      setCookie(COOKIE_NAME, b, COOKIE_DAYS);
      return { slug: b, source: "localStorage_restore" };
    }
    return null;
  }

  // Kept for callers of window.NPAvatarSwap.getAvatarSlug(): the RESOLVED
  // slug (URL, then cookie, then localStorage), or null.
  function getAvatarSlug() {
    var r = resolveAvatar();
    return r ? r.slug : null;
  }

  function apply() {
    var resolved = resolveAvatar();
    if (!resolved) return; // no param and nothing stored -> default content stands, untouched
    var slug = resolved.slug;
    var block = AVATAR_BLOCKS[slug];
    if (!block) return; // cannot happen (isValidSlug), kept as the hard no-op guard

    // Headline swap. If the block names a `key` word, wrap it in a neutral
    // .hl-key span (escaped, built via DOM nodes — no innerHTML of copy) so
    // np-spritz.js's author-marked squiggle pass underlines THAT word instead
    // of falling back to the final word ("Either."/"Drawer"/"Sedated." bug,
    // Jared report 2026-09-01).
    //
    // 2026-09-01 HIERARCHY FLIP (Jared, this thread — global, every avatar):
    // the avatar hook goes into the small-caps EYEBROW (#np-eyebrow, accent
    // color, squiggle under `key`), and the big serif h2 (#np-headline) becomes
    // the PRODUCT NAME. Earlier today it was the reverse (hook huge, product
    // name a tiny caps line in #np-prodname underneath) — pdp_headline_before.jpg.
    // "Natures Relax Gummies" is deliberately NOT in the h2: it's already in
    // the site header and URL. The old #np-prodname line stays hidden.
    var hookEl = document.getElementById("np-eyebrow");
    var headlineEl = document.getElementById("np-headline");
    var hookTarget = hookEl || headlineEl; // eyebrow missing (older markup) -> old behaviour
    if (hookTarget) {
      fillWithKey(hookTarget, block.headline, block.key);
    }
    if (hookEl && headlineEl) {
      hookEl.hidden = false;
      // Product name with "Natures Relax" marked as the squiggle key word
      // (2026-09-01 PM). data-sp-skip stays on as a belt-and-braces guard so
      // pass-B can never fall back to underlining the final word "Gummies".
      fillWithKey(headlineEl, block.product_name || PRODUCT_NAME, block.product_key || PRODUCT_KEY);
      headlineEl.setAttribute("data-sp-skip", "1");
    }

    // #np-prodname (the 08-31 tiny caps product-name line): superseded by the
    // h2 now carrying the product name. Keep hidden. Only un-hide on legacy
    // markup that has no eyebrow slot (h2 still holds the hook there).
    var prodName = document.getElementById("np-prodname");
    if (prodName) prodName.hidden = !!hookEl;

    var subEl = document.getElementById("np-sub");
    if (subEl) subEl.textContent = block.sub;

    // Hero image: ACTIVATE an existing real-product-photo frame in the gallery
    // (and its matching thumb + caption) instead of injecting a new node. Same
    // .on class toggling assets/np.js's show(i) does, done synchronously here
    // before the rest of the page paints. Frame count never changes, so the
    // gallery's thumb->frame index mapping stays valid and clicks keep working.
    var gallery = document.querySelector("[data-gallery]");
    var stage = gallery && gallery.querySelector(".bm-stage");
    if (stage) {
      var frames = stage.querySelectorAll(":scope > img, :scope > video");
      var thumbs = gallery.querySelectorAll(".bm-th");
      var cap = gallery.querySelector(".bm-cap");
      // Per-avatar frame re-pointing (steady-operator men-focused photos,
      // 2026-09-01 PM) runs BEFORE the hero pick so a swapped frame's new src
      // and caption are what pickFrame/the caption line see.
      var swapped = applyFrameSwaps(frames, thumbs, block.frame_swaps);
      if (swapped) {
        try { document.documentElement.setAttribute("data-avatar-frames-swapped", String(swapped)); } catch (e) { /* non-fatal */ }
      }
      var idx = pickFrame(frames, block.frames || HERO_FRAMES);
      if (idx !== -1) {
        for (var f = 0; f < frames.length; f++) {
          var isOn = f === idx;
          frames[f].classList.toggle("on", isOn);
          if (frames[f].tagName === "VIDEO") {
            try {
              if (isOn) {
                // Belt and braces for autoplay: attributes are already on the
                // tag (autoplay muted loop playsinline), but set the properties
                // too and nudge play() so the loop runs without a tap.
                frames[f].muted = true; frames[f].loop = !frames[f].hasAttribute("data-np-stopat");
                frames[f].setAttribute("playsinline", "");
                /* SPEED 20261028: a lazy (preload="none") frame is left to np-lazy-video.js, which arms and
                   plays it when the gallery scrolls near. Setting autoplay/play() here pulled the 1.9 MB
                   clip at parse time, twice, on every avatar page. */
                var lz = frames[f].getAttribute("data-np-lazy");
                if (!lz || lz === "ready") {
                  frames[f].autoplay = true;
                  var pr = frames[f].play();
                  if (pr && pr.catch) pr.catch(function () {});
                }
              } else {
                frames[f].pause();
              }
            } catch (e) { /* non-fatal */ }
          }
          if (thumbs.length === frames.length) {
            thumbs[f].classList.toggle("on", isOn);
            thumbs[f].setAttribute("aria-current", isOn ? "true" : "false");
          }
        }
        frames[idx].setAttribute("data-avatar-swapped", slug);
        if (cap) cap.textContent = frames[idx].getAttribute("data-cap") || cap.textContent;
      }
    }

    // Optional testimonial: only touch it if the theme has a designated slot
    // for one ([data-avatar-quote]) — none exists in the shipped markup yet,
    // so this is a safe forward-compat no-op today, not a broken selector.
    var quoteEl = document.querySelector("[data-avatar-quote]");
    if (quoteEl && block.quote) quoteEl.textContent = block.quote;

    // 2026-10-24: avatar "sounds like you" block + Landra hook on the product template only.
    applyFit(slug);

    try {
      document.documentElement.setAttribute("data-avatar-active", slug);
      document.documentElement.setAttribute("data-avatar-source", resolved.source);
    } catch (e) { /* non-fatal */ }

    // Analytics exposure, same shape as np-offer-assign.js.
    try {
      window.NP_AVATAR = slug;
      window.NP_AVATAR_SOURCE = resolved.source;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "np_avatar_applied", np_avatar: slug, np_avatar_source: resolved.source });
    } catch (e) { /* non-fatal */ }
  }

  window.NPAvatarSwap = {
    AVATAR_BLOCKS: AVATAR_BLOCKS,
    AVATAR_FIT: AVATAR_FIT,
    getAvatarSlug: getAvatarSlug,
    resolveAvatar: resolveAvatar,
    apply: apply
  };
})();
